/**
 * Branded type for participant primary contact information.
 * @example
 * ```typescript
 * // Creating with email only
 * const contact = ParticipantPrimaryContact.create({
 *   primaryEmail: EmailAddress.create('user@example.com'),
 *   primaryPhone: null,
 * });
 * // Both null is valid (lenient import)
 * const lenient = ParticipantPrimaryContact.create({
 *   primaryEmail: null,
 *   primaryPhone: null,
 * });
 * // Use tryCreate() when at least one contact is required
 * const strict = ParticipantPrimaryContact.tryCreate({
 *   primaryEmail: null,
 *   primaryPhone: null,
 * }); // returns undefined
 * ```
 */

import { z } from "zod";
import { EmailAddressSchema, type EmailAddress } from "./email.schemas.js";
import {
  E164PhoneNumberSchema,
  type E164PhoneNumber,
} from "./phone.schemas.js";

export interface ParticipantPrimaryContactInput {
  readonly primaryEmail: EmailAddress | null;
  readonly primaryPhone: E164PhoneNumber | null;
}

export const ParticipantPrimaryContactSchema = z
  .object({
    primaryEmail: EmailAddressSchema.nullable(),
    primaryPhone: E164PhoneNumberSchema.nullable(),
  })
  .brand("ParticipantPrimaryContact");

/**
 * Participant primary contact type.
 * Both-null is permitted for lenient imports (invalid values tracked in invalidFields).
 * Use `isValid()` / `tryCreate()` when the strict "at least one" invariant is needed.
 */

export type ParticipantPrimaryContact = z.infer<
  typeof ParticipantPrimaryContactSchema
>;

function hasAtLeastOneContact(data: ParticipantPrimaryContactInput): boolean {
  return data.primaryEmail !== null || data.primaryPhone !== null;
}

export const ParticipantPrimaryContact = {
  create(data: ParticipantPrimaryContactInput): ParticipantPrimaryContact {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return {
      primaryEmail: data.primaryEmail,
      primaryPhone: data.primaryPhone,
    } as ParticipantPrimaryContact;
  },

  unsafeCreate(
    data: ParticipantPrimaryContactInput,
  ): ParticipantPrimaryContact {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return {
      primaryEmail: data.primaryEmail,
      primaryPhone: data.primaryPhone,
    } as ParticipantPrimaryContact;
  },

  isValid(data: ParticipantPrimaryContactInput): boolean {
    return hasAtLeastOneContact(data);
  },

  /**
   * Try to create a ParticipantPrimaryContact, returning undefined if invalid.
   * @param data - Contact data
   * @returns ParticipantPrimaryContact if valid, undefined otherwise
   */
  tryCreate(
    data: ParticipantPrimaryContactInput,
  ): ParticipantPrimaryContact | undefined {
    if (!hasAtLeastOneContact(data)) {
      return undefined;
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return {
      primaryEmail: data.primaryEmail,
      primaryPhone: data.primaryPhone,
    } as ParticipantPrimaryContact;
  },

  /**
   * Get the primary identifier for the contact.
   * Returns email if available, otherwise phone, or null if both are absent (lenient import with invalid contact data).
   * @param contact - The contact to get identifier from
   * @returns The primary identifier (email or phone as string), or null
   */
  getPrimaryIdentifier(contact: ParticipantPrimaryContact): string | null {
    if (contact.primaryEmail !== null) {
      return String(contact.primaryEmail);
    }
    if (contact.primaryPhone !== null) {
      return String(contact.primaryPhone);
    }
    return null;
  },
} as const;
